import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useInheritDocumentsMutation,
  useSearchDocumentsMutation,
} from "../../../../api/services/client/clientApis";
import {
  SearchDocumentResponse,
  SearchDocumentResultRow,
  SearchInheritDocumentsPayload,
} from "../../../../api/services/client/models";
import { MuiModal, STATUS_BADGE_TYPE } from "../../../../components";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import {
  getFieldUndefinedForPayloadParams,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useEnterKeyAction } from "../../../../hooks/useEnterKeyAction";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { InheritDocumentsSearchFormKey } from "../../modelsAndSchemas/Models";
import { InheritDocumentsValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { inheritDocumentSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { InheritDocumentsResult } from "./components/inheritDocumentsResult/InheritDocumentsResult";
import { InheritDocumentsSearchForm } from "./components/inheritDocumentsSearchForm/InheritDocumentsSearchForm";

interface InheritDocumentsProps {
  open: boolean;
  onClose: () => void;
  onDocumentsInherited?: () => void;
}

export const InheritDocuments: React.FC<InheritDocumentsProps> = ({
  open,
  onClose,
  onDocumentsInherited,
}) => {
  const { useFetchLabel, commonLabels, fetchComponentTitle } = useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [searchResultList, setSearchResultList] = useState<
    SearchDocumentResponse[]
  >([]);
  const [selectedDocuments, setSelectedDocuments] = useState<
    SearchDocumentResultRow[]
  >([]);
  const defaultValues = {
    [InheritDocumentsSearchFormKey.documentName]: "",
    [InheritDocumentsSearchFormKey.contractId]: "",
    [InheritDocumentsSearchFormKey.documentType]: "",
  };

  const {
    mutateAsync: searchDocuments,
    data: searchDocumentsList,
    isPending: isSearchDocumentsPending,
    isSuccess: isSearchDocumentsSuccess,
    isError: isSearchDocumentsError,
  } = useSearchDocumentsMutation();

  const {
    mutateAsync: inheritDocuments,
    isPending: isInheritDocumentsPending,
    isSuccess: isInheritDocumentsSuccess,
    isError: isInheritDocumentsError,
  } = useInheritDocumentsMutation();

  const { id: clientId } = useSelector(createClientSelector);

  const {
    inheritDocumentTitle,
    inheritButton,
    documentsInheritedToastMessage,
    searchDocumentNameMustBeAtLeast3Characters,
  } = inheritDocumentSiteCoreKeys;

  const getLabel = useFetchLabel(inheritDocumentTitle);

  const methods = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      InheritDocumentsValidationSchema({
        min3CharsValidation: getLabel(
          searchDocumentNameMustBeAtLeast3Characters,
        ),
      }),
    ),
  });
  const { getValues, handleSubmit, reset } = methods;

  useEffect(() => {
    if (isSearchDocumentsSuccess && isNotEmpty(searchDocumentsList)) {
      setSearchResultList(searchDocumentsList);
    }
  }, [searchDocumentsList, isSearchDocumentsSuccess]);

  useEffect(() => {
    if (isInheritDocumentsSuccess) {
      displayToast(getLabel(documentsInheritedToastMessage), "success");
      onClose();
      onDocumentsInherited?.();
    }
  }, [isInheritDocumentsSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        searchDocumentsLoader: {
          isVisible: isSearchDocumentsPending || isInheritDocumentsPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          searchDocumentsLoader: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isSearchDocumentsPending, isInheritDocumentsPending]);

  useEffect(() => {
    if (isSearchDocumentsError || isInheritDocumentsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ searchDocumentsLoader: { isVisible: false } }));
    }
  }, [isSearchDocumentsError, isInheritDocumentsError]);

  const handleInheritButtonClick = () => {
    if (isNotEmpty(clientId) && isNotEmpty(selectedDocuments)) {
      const documentsToInherit = selectedDocuments
        .filter((doc) => doc.isChecked && !doc.isInherited)
        .map((doc) => ({
          documentId: doc.document.id,
          documentType: doc.documentType,
        }));
      inheritDocuments({
        clientId,
        documents: documentsToInherit,
      });
    }
  };

  const handleClearAllSearch = () => {
    reset({
      ...defaultValues,
    });
    setIsSearchClick(false);
    setSearchResultList([]);
  };

  useEnterKeyAction({
    onEnter: handleSubmit(() => handleSearchClick()),
  });

  useEffect(() => {
    handleSearchClick();
  }, []);

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    const { documentName, contractId, documentType } = getValues();
    const params: SearchInheritDocumentsPayload = {
      clientId,
      statusIds: [STATUS_BADGE_TYPE.active, STATUS_BADGE_TYPE.autoRenewed],
      searchInParentsOnly: true,
      ...(isNotEmpty(contractId) && { contractId: Number(contractId) }),
      ...getFieldUndefinedForPayloadParams({
        documentName,
        documentType,
      }),
    };
    searchDocuments(params);
  };

  return (
    <MuiModal
      id={COMPONENT_IDS.INHERIT_DOCUMENTS_MODAL}
      hasLargeContent
      open={open}
      onClose={onClose}
      title={fetchComponentTitle(inheritDocumentTitle)}
      primaryButtonTitle={getLabel(inheritButton)}
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={handleInheritButtonClick}
      onSecondaryButtonClick={onClose}
    >
      <>
        <FormProvider {...methods}>
          <InheritDocumentsSearchForm
            handleClearAllSearch={handleClearAllSearch}
            handleSearchClick={() => handleSubmit(handleSearchClick)()}
          />
        </FormProvider>

        <InheritDocumentsResult
          rows={searchResultList}
          isSearch={isSearchClick}
          setSelectedDocuments={setSelectedDocuments}
        />
      </>
    </MuiModal>
  );
};
